// amapService.tsx

// Define interfaces for type safety
interface Tip {
  name: string;
  district: string;
  address: string;
  location: string; // e.g., "lng,lat"
  // Add other fields based on API response
}

interface InputTipsResponse {
  status: string;
  count: string;
  tips: Tip[];
  info: string;
  // Add other fields based on API response
}

interface Step {
  polyline: string;
  // Add other fields based on API response
}

interface Path {
  steps: Step[];
  // Add other fields based on API response
}

interface Route {
  paths: Path[];
  // Add other fields based on API response
}

interface DrivingRouteResponse {
  status: string;
  info: string;
  route?: Route;
  // Add other fields based on API response
}

interface FetchInputTipsParams {
  keywords: string;
  apiKey: string;
  cityLimit?: boolean;
}

interface FetchDrivingRouteParams {
  origin: string;
  destination: string;
  apiKey: string;
}

// Custom error class for better error handling
class AmapServiceError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = "AmapServiceError";
  }
}

// Existing function for fetching input tips (from previous task)
export const fetchInputTips = async ({
                                       keywords,
                                       apiKey,
                                       cityLimit = true,
                                     }: FetchInputTipsParams): Promise<InputTipsResponse> => {
  try {
    if (!keywords || !apiKey) {
      throw new AmapServiceError("Missing required parameters: keywords or apiKey");
    }

    const url = new URL("https://restapi.amap.com/v3/assistant/inputtips");
    url.searchParams.append("key", apiKey);
    url.searchParams.append("citylimit", cityLimit.toString());
    url.searchParams.append("keywords", keywords);

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new AmapServiceError(`HTTP error! Status: ${response.status}`, response.status);
    }

    const data: InputTipsResponse = await response.json();
    if (data.status !== "1") {
      throw new AmapServiceError(`Amap API error: ${data.info || "Unknown error"}`);
    }
    // console.log('inputtips: ', data);
    return data;
  } catch (error) {
    if (error instanceof AmapServiceError) {
      console.error(`AmapServiceError: ${error.message}`, { status: error.status });
      throw error;
    } else if (error instanceof Error) {
      console.error("Unexpected error fetching Amap input tips:", error.message);
      throw new AmapServiceError("Failed to fetch Amap input tips due to an unexpected error");
    } else {
      console.error("Unknown error:", error);
      throw new AmapServiceError("An unknown error occurred");
    }
  }
};

// New function for fetching driving route
export const fetchDrivingRoute = async ({
                                          origin,
                                          destination,
                                          apiKey,
                                        }: FetchDrivingRouteParams): Promise<string> => {
  try {
    // Validate parameters
    if (!origin || !destination || !apiKey) {
      throw new AmapServiceError("Missing required parameters: origin, destination, or apiKey");
    }

    // Construct the URL
    const url = new URL("https://restapi.amap.com/v3/direction/driving");
    url.searchParams.append("origin", origin);
    url.searchParams.append("destination", destination);
    url.searchParams.append("key", apiKey);

    // Make API request
    const response = await fetch(url.toString());

    // Check for HTTP errors
    if (!response.ok) {
      throw new AmapServiceError(`HTTP error! Status: ${response.status}`, response.status);
    }

    // Parse response
    const data: DrivingRouteResponse = await response.json();

    // Check for API-level errors
    if (data.status !== "1") {
      throw new AmapServiceError(`Amap API error: ${data.info || "Unknown error"}`);
    }

    // Extract polyline
    const steps = data?.route?.paths?.[0]?.steps;
    if (!steps?.length) {
      throw new AmapServiceError("No driving route steps found");
    }

    return  steps.map((step: Step) => step.polyline).join(";");;
  } catch (error) {
    if (error instanceof AmapServiceError) {
      console.error(`AmapServiceError: ${error.message}`, { status: error.status });
      throw error;
    } else if (error instanceof Error) {
      console.error("Unexpected error fetching Amap driving route:", error.message);
      throw new AmapServiceError("Failed to fetch Amap driving route due to an unexpected error");
    } else {
      console.error("Unknown error:", error);
      throw new AmapServiceError("An unknown error occurred");
    }
  }
};